// ============================================================================
// EduPath Sprint 5 - Offline Sync Store (Zustand)
// ============================================================================
// Reactive state management for offline sync status.
// Bridges the syncManager service with React components.
// ============================================================================

import { create } from 'zustand';
import { syncManager } from '@/services/syncManager';
import { offlineQueue } from '@/services/offlineQueueService';
import type {
  OfflineQueueItem,
  SyncStatus,
  SyncEvent,
  ConflictResolution,
  AssessmentResponsePayload,
  OfflineSessionMeta,
} from '@/types/offline';

// ─── Store Interface ─────────────────────────────────────────────────────────

interface OfflineSyncState {
  /** Current sync status */
  syncStatus: SyncStatus;
  /** All queue items for display */
  queueItems: OfflineQueueItem[];
  /** Whether the sync manager has been initialized */
  isInitialized: boolean;
  /** Most recent sync event */
  lastEvent: SyncEvent | null;
  /** Whether the conflict resolution modal is open */
  conflictModalOpen: boolean;
  /** Item being resolved in conflict modal */
  conflictItem: OfflineQueueItem | null;
  /** Toast notification message */
  toastMessage: { type: 'success' | 'error' | 'info'; text: string } | null;

  // ── Actions ──────────────────────────────────────────────────────────────

  /** Initialize the sync manager. Call once at app startup. */
  initialize: () => void;

  /** Refresh sync status and queue items from IndexedDB. */
  refresh: () => Promise<void>;

  /** Enqueue a completed assessment for offline sync. */
  enqueueAssessment: (params: {
    sessionId: string;
    learnerId: string;
    assessmentType: string;
    idempotencyKey: string;
    responses: AssessmentResponsePayload[];
    sessionMeta: OfflineSessionMeta;
  }) => Promise<OfflineQueueItem>;

  /** Trigger an immediate sync. */
  syncNow: () => Promise<void>;

  /** Retry a failed item. */
  retryItem: (itemId: string) => Promise<void>;

  /** Discard a failed/rejected item. */
  discardItem: (itemId: string) => Promise<void>;

  /** Resolve a conflict. */
  resolveConflict: (
    itemId: string,
    resolution: ConflictResolution
  ) => Promise<void>;

  /** Open conflict resolution modal for an item. */
  openConflictModal: (item: OfflineQueueItem) => void;

  /** Close conflict resolution modal. */
  closeConflictModal: () => void;

  /** Clear toast message. */
  clearToast: () => void;

  /** Pause sync operations. */
  pauseSync: () => void;

  /** Resume sync operations. */
  resumeSync: () => void;
}

// ─── Default State ───────────────────────────────────────────────────────────

const defaultSyncStatus: SyncStatus = {
  state: 'IDLE',
  pendingCount: 0,
  syncingCount: 0,
  failedCount: 0,
  rejectedCount: 0,
  conflictCount: 0,
  lastSyncAt: null,
  isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
  nextRetryAt: null,
};

// ─── Store ───────────────────────────────────────────────────────────────────

export const useOfflineSyncStore = create<OfflineSyncState>((set, get) => ({
  syncStatus: defaultSyncStatus,
  queueItems: [],
  isInitialized: false,
  lastEvent: null,
  conflictModalOpen: false,
  conflictItem: null,
  toastMessage: null,

  initialize: () => {
    if (get().isInitialized) return;

    // Subscribe to sync events
    syncManager.onEvent((event: SyncEvent) => {
      set({ lastEvent: event });

      // Auto-refresh on state changes
      get().refresh();

      // Show toast notifications for key events
      switch (event.type) {
        case 'ITEM_SYNCED':
          set({
            toastMessage: {
              type: 'success',
              text: `Assessment synced successfully`,
            },
          });
          break;
        case 'ITEM_REJECTED':
          set({
            toastMessage: {
              type: 'error',
              text: event.error || 'Assessment submission was rejected',
            },
          });
          break;
        case 'OFFLINE':
          set({
            toastMessage: {
              type: 'info',
              text: 'You are offline. Submissions will sync when connected.',
            },
          });
          break;
        case 'ONLINE':
          set({
            toastMessage: {
              type: 'info',
              text: 'Back online. Syncing pending submissions...',
            },
          });
          break;
      }
    });

    // Start the sync manager
    syncManager.start();

    // Initial data load
    get().refresh();

    set({ isInitialized: true });
  },

  refresh: async () => {
    try {
      const [syncStatus, queueItems] = await Promise.all([
        syncManager.getStatus(),
        syncManager.getQueueItems(),
      ]);

      set({
        syncStatus,
        queueItems: queueItems.sort(
          (a, b) =>
            new Date(b.queuedAt).getTime() - new Date(a.queuedAt).getTime()
        ),
      });
    } catch (err) {
      console.error('[OfflineSyncStore] Refresh error:', err);
    }
  },

  enqueueAssessment: async (params) => {
    const item = await offlineQueue.enqueue(params);
    await get().refresh();

    // Trigger immediate sync if online
    if (navigator.onLine) {
      syncManager.syncNow();
    }

    return item;
  },

  syncNow: async () => {
    await syncManager.syncNow();
    await get().refresh();
  },

  retryItem: async (itemId: string) => {
    await syncManager.retryItem(itemId);
    await get().refresh();
  },

  discardItem: async (itemId: string) => {
    await syncManager.discardItem(itemId);
    set({
      conflictModalOpen: false,
      conflictItem: null,
      toastMessage: { type: 'info', text: 'Submission discarded' },
    });
    await get().refresh();
  },

  resolveConflict: async (itemId: string, resolution: ConflictResolution) => {
    switch (resolution) {
      case 'RETRY':
        await syncManager.retryItem(itemId);
        set({
          toastMessage: { type: 'info', text: 'Retrying submission...' },
        });
        break;
      case 'DISCARD':
        await syncManager.discardItem(itemId);
        set({
          toastMessage: { type: 'info', text: 'Submission discarded' },
        });
        break;
      case 'SAVE_LOCAL':
        // Just close the modal - item stays in queue
        set({
          toastMessage: {
            type: 'info',
            text: 'Saved locally. You can retry later.',
          },
        });
        break;
    }

    set({ conflictModalOpen: false, conflictItem: null });
    await get().refresh();
  },

  openConflictModal: (item: OfflineQueueItem) => {
    set({ conflictModalOpen: true, conflictItem: item });
  },

  closeConflictModal: () => {
    set({ conflictModalOpen: false, conflictItem: null });
  },

  clearToast: () => {
    set({ toastMessage: null });
  },

  pauseSync: () => {
    syncManager.pause();
    get().refresh();
  },

  resumeSync: () => {
    syncManager.resume();
    get().refresh();
  },
}));

export default useOfflineSyncStore;
